/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListAudienceOptionsRequest,
  ListAudienceOptionsSecurity,
} from "../models/operations/listaudienceoptions.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  AudienceOptionsQueryData,
  buildAudienceOptionsQuery,
  prefetchAudienceOptions,
  queryKeyAudienceOptions,
} from "./audienceOptions.core.js";
export {
  type AudienceOptionsQueryData,
  buildAudienceOptionsQuery,
  prefetchAudienceOptions,
  queryKeyAudienceOptions,
};

export type AudienceOptionsQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listAudienceOptions access
 *
 * @remarks
 * List the principals that can be given access: everyone, roles, people, and agents.
 */
export function useAudienceOptions(
  request?: ListAudienceOptionsRequest | undefined,
  security?: ListAudienceOptionsSecurity | undefined,
  options?: QueryHookOptions<
    AudienceOptionsQueryData,
    AudienceOptionsQueryError
  >,
): UseQueryResult<AudienceOptionsQueryData, AudienceOptionsQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildAudienceOptionsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listAudienceOptions access
 *
 * @remarks
 * List the principals that can be given access: everyone, roles, people, and agents.
 */
export function useAudienceOptionsSuspense(
  request?: ListAudienceOptionsRequest | undefined,
  security?: ListAudienceOptionsSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    AudienceOptionsQueryData,
    AudienceOptionsQueryError
  >,
): UseSuspenseQueryResult<AudienceOptionsQueryData, AudienceOptionsQueryError> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildAudienceOptionsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setAudienceOptionsData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      gramKey?: string | undefined;
      gramSession?: string | undefined;
    },
  ],
  data: AudienceOptionsQueryData,
): AudienceOptionsQueryData | undefined {
  const key = queryKeyAudienceOptions(...queryKeyBase);

  return client.setQueryData<AudienceOptionsQueryData>(key, data);
}

export function invalidateAudienceOptions(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      gramKey?: string | undefined;
      gramSession?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "access",
      "listAudienceOptions",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllAudienceOptions(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "access", "listAudienceOptions"],
  });
}
